import { useTranslation } from 'react-i18next';

import type { V1CodeManifest } from '@/generated/hooks/grimoire/models';
import { ManifestSopExportMenuItems } from '@/modules/automation/components/AutomationEditor/ManifestSopExportMenuItems';
import { useNavigate } from 'react-router-dom';
import { useBoolean } from 'usehooks-ts';

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
} from '@kognitos/lattice';

import { V1AutomationStage } from '@/generated/hooks/grimoire/models/v1AutomationStage';
import { AutomationDeleteDialog } from '@/modules/automation/components/AutomationDeleteDialog';
import { AutomationOpenDraftDialog } from '@/modules/automation/components/AutomationOpenDraftDialog';
import { DuplicateAutomationDialog } from '@/modules/automation/components/DuplicateAutomationDialog';
import { DraftDeleteDialog } from '@/modules/drafts/components/DraftDeleteDialog';
import { usePermissions } from '@/shared/permissions';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores/user-preferences.store';
import { getRoutePath } from '@/routes/util';

interface IDocumentTitleMenuProps {
  /** The automation this menu acts on. Omitted renders nothing. */
  automationId?: string;
  /** Display name, shown in the confirmation dialogs. */
  title: string;
  /** Draft vs published, which picks the delete/duplicate flavour. */
  isDraft?: boolean;
  /** Current version, so the delete dialog can scope its active-run warning. */
  version?: string;
  /**
   * Puts the inline title into edit mode. `Rename` here focuses the field in
   * the title bar rather than opening a dialog, so the name is only ever
   * edited in the one place it is displayed.
   */
  onRename?: () => void;
  /**
   * Export the automation. The spec keeps export out of this menu, but the
   * draft editor's export affordance lived on the toolbar row the title bar
   * replaces — supplying this keeps that capability reachable. Omit it and no
   * export item renders.
   */
  onExport?: () => void;
  /**
   * The built automation's manifest, which enables "Export as Markdown".
   * Omitted (no manifest yet, or a stale one) hides that item rather than
   * offering an export that would produce an empty document.
   */
  exportManifest?: V1CodeManifest;
  /** Used for the download filename when the manifest carries no title. */
  exportTitleFallback?: string;
  /**
   * `title` is the chevron beside the name; `overflow` is a vertical three-dot button at the
   * end of an action row, opening toward the row's end.
   */
  placement?: 'title' | 'overflow';
  /**
   * Offers "Edit as Draft" on a live automation, which copies it into a new
   * draft. `hasDraft` makes the dialog warn that it replaces the existing one.
   */
  editAsDraft?: { hasDraft: boolean; isDeactivated?: boolean };
  /**
   * Offers "Discard draft" while an automation has both a live version and a
   * draft. Delete then removes the whole automation, on either version.
   */
  discardDraft?: { version?: string; onDiscarded: () => void };
}

/**
 * Overflow menu for the document title bar: `Edit as Draft` on a live
 * automation, `Rename`, `Duplicate`, `Discard draft` beside a live version,
 * and `Delete`.
 *
 * Deliberately not `AutomationCardMenu`: that menu renames through a dialog
 * (this one focuses the inline title), always offers `Move to…` with no way to
 * suppress it, and carries publish/export/support-access items the document
 * title bar does not want.
 */
export function DocumentTitleMenu({
  automationId,
  title,
  isDraft = true,
  version,
  onRename,
  onExport,
  exportManifest,
  exportTitleFallback,
  placement = 'title',
  editAsDraft,
  discardDraft,
}: IDocumentTitleMenuProps) {
  const { t: tAutomation } = useTranslation('automation');
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const permissionScope = {
    organization: currentOrganizationId,
    workspace: currentWorkspaceId,
  };
  const canDelete = hasPermission(['DeleteAutomation'], permissionScope);
  const canDuplicate = hasPermission(
    ['ForkPublishedAutomation'],
    permissionScope,
  );
  const canRename = hasPermission(['RenameAutomation'], permissionScope);

  const {
    value: isDeleteDialogOpen,
    setTrue: openDeleteDialog,
    setFalse: closeDeleteDialog,
  } = useBoolean();

  const {
    value: isDuplicateDialogOpen,
    setTrue: openDuplicateDialog,
    setFalse: closeDuplicateDialog,
  } = useBoolean();

  const {
    value: isEditAsDraftDialogOpen,
    setTrue: openEditAsDraftDialog,
    setFalse: closeEditAsDraftDialog,
  } = useBoolean();

  const {
    value: isDiscardDialogOpen,
    setTrue: openDiscardDialog,
    setFalse: closeDiscardDialog,
  } = useBoolean();

  if (!automationId) return null;

  const deletesDraftOnly = isDraft && !discardDraft;

  // The document you were reading no longer exists, so don't leave the editor
  // sitting on it.
  const handleDeleted = () => {
    navigate(
      getRoutePath(deletesDraftOnly ? 'DRAFTS' : 'AUTOMATIONS', {
        orgId: currentOrganizationId,
        workspaceId: currentWorkspaceId,
      }),
    );
  };

  return (
    <>
      {/* Not modal: a modal menu traps focus, and `Rename` moves focus to the
          title field outside it. */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-xs"
            className="rounded-md text-muted-foreground data-[state=open]:bg-muted hover:text-foreground"
            aria-label={tAutomation('automation.moreOptions')}
            data-testid="document-title-menu-trigger"
          >
            <Icon
              type={
                placement === 'overflow' ? 'EllipsisVertical' : 'ChevronDown'
              }
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={placement === 'overflow' ? 'end' : 'start'}
          sideOffset={4}
          className="w-48"
          // `Rename` has already moved focus to the title field; letting Radix
          // hand focus back to this trigger would blur it and commit the edit.
          onCloseAutoFocus={event => event.preventDefault()}
        >
          {editAsDraft && (
            <>
              <DropdownMenuItem
                disabled={!canDuplicate}
                onClick={openEditAsDraftDialog}
                data-testid="document-title-menu-edit-as-draft"
              >
                {tAutomation('automation.header.editAsDraft')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {onRename && (
            <>
              <DropdownMenuItem
                disabled={!canRename}
                onClick={onRename}
                data-testid="document-title-menu-rename"
              >
                {tAutomation('automation.rename')}
              </DropdownMenuItem>
              {/* Tied to the item above it: on its own it opens a read-only
                  menu with a divider and nothing to divide. */}
              <DropdownMenuSeparator />
            </>
          )}
          {onExport && (
            <DropdownMenuItem
              onClick={onExport}
              data-testid="document-title-menu-export"
            >
              {tAutomation('automation.exportToZip')}
            </DropdownMenuItem>
          )}
          {exportManifest && (
            <ManifestSopExportMenuItems
              manifest={exportManifest}
              titleFallback={exportTitleFallback}
              withIcon={false}
            />
          )}
          {(onExport || exportManifest) && <DropdownMenuSeparator />}
          {/* Delete is always last, after the copy that sits beside it. */}
          <DropdownMenuItem
            disabled={!canDuplicate}
            onClick={openDuplicateDialog}
            data-testid="document-title-menu-duplicate"
          >
            {tAutomation('automation.duplicate')}
          </DropdownMenuItem>
          {discardDraft && (
            <DropdownMenuItem
              variant="destructive"
              disabled={!canDelete}
              onClick={openDiscardDialog}
              data-testid="document-title-menu-discard-draft"
            >
              {tAutomation('automation.discardDraft')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            variant="destructive"
            disabled={!canDelete}
            onClick={openDeleteDialog}
            data-testid="document-title-menu-delete"
          >
            {tAutomation('automation.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Mounted on demand: these dialogs each pull in their own mutations and
          queries, and the title bar renders on every draft. */}
      {discardDraft && isDiscardDialogOpen && (
        <DraftDeleteDialog
          open
          isDiscard
          onClose={closeDiscardDialog}
          title={title}
          draftId={automationId}
          currentVersion={discardDraft.version}
          onDelete={discardDraft.onDiscarded}
        />
      )}

      {isDeleteDialogOpen &&
        (deletesDraftOnly ? (
          <DraftDeleteDialog
            open
            onClose={closeDeleteDialog}
            title={title}
            draftId={automationId}
            currentVersion={version}
            onDelete={handleDeleted}
          />
        ) : (
          <AutomationDeleteDialog
            open
            onClose={closeDeleteDialog}
            title={title}
            automationId={automationId}
            onDelete={handleDeleted}
          />
        ))}

      {editAsDraft && isEditAsDraftDialogOpen && (
        <AutomationOpenDraftDialog
          open
          onClose={closeEditAsDraftDialog}
          automationId={automationId}
          isOverride={editAsDraft.hasDraft}
          isDeactivated={editAsDraft.isDeactivated}
        />
      )}

      {isDuplicateDialogOpen && (
        <DuplicateAutomationDialog
          open
          onClose={closeDuplicateDialog}
          title={title}
          automationId={automationId}
          stage={
            isDraft
              ? V1AutomationStage.AUTOMATION_STAGE_DRAFT
              : V1AutomationStage.AUTOMATION_STAGE_PUBLISHED
          }
        />
      )}
    </>
  );
}
